import type { SearchDataModel } from "~/types/models/SearchData.model";
import { useApiService } from "./useApiService";
import type { IArticle, IAuthor, IRecommendedResearchTopic } from "~/types";
import type { IOpenForSubmissionResponse } from "~/types/IOpenForSubmissionCard";
import type { IListResponse } from "~/types/IListResponse";

export const useRtApiService = () => {
  const { getFetchData, postFetchData } = useApiService();

  const getArticlesByResearchTopicId = async (
    researchTopicId: number,
    params: SearchDataModel
  ): Promise<IArticle[]> => {
    return await postFetchData<IArticle[]>(
      `/api/v3/research-topics/${researchTopicId}/articles`,
      params
    );
  };

  const getAuthorsByResearchTopicId = async (
    researchTopicId: number,
    params: SearchDataModel
  ): Promise<IListResponse<IAuthor>> => {
    const articleAuthors = await getFetchData<IListResponse<IAuthor>>(
      `/api/v3/research-topics/${researchTopicId}/authors?offset=${params.skip}&limit=${params.limit}`
    );
    return articleAuthors;
  };

  const getTotalArticles = async (researchTopicId: number): Promise<number> => {
    const total = await getFetchData<number>(
      `/api/v3/research-topics/${researchTopicId}/articles/count`
    );
    return total;
  };

  const getAllAuthorsByResearchTopicId = async (
    researchTopicId: number,
    params: { skip: number; limit: number }
  ): Promise<IListResponse<IAuthor>> => {
    return await getFetchData<IListResponse<IAuthor>>(
      `/api/v3/research-topics/${researchTopicId}/authors/all?offset=${params.skip}&limit=${params.limit}`
    );
  };

  const getOpenForSubmission = async (
    researchTopicId: number
  ): Promise<IOpenForSubmissionResponse> => {
    return await getFetchData<IOpenForSubmissionResponse>(
      `/api/v3/research-topics/${researchTopicId}/open-for-submission`
    );
  };

  const getTotalAuthors = async (researchTopicId: number): Promise<number> => {
    const total = await getFetchData<number>(
      `/api/v3/research-topics/${researchTopicId}/authors/count`
    );
    return total;
  };

  const getRecommendedResearchTopics = async (
    researchTopicId: number
  ): Promise<IRecommendedResearchTopic[]> => {
    return await getFetchData<IRecommendedResearchTopic[]>(
      `/api/v3/research-topics/${researchTopicId}/recommended`
    );
  };

  return {
    getArticlesByResearchTopicId,
    getAuthorsByResearchTopicId,
    getAllAuthorsByResearchTopicId,
    getOpenForSubmission,
    getTotalArticles,
    getTotalAuthors,
    getRecommendedResearchTopics
  };
};
